import React, {useCallback, useMemo, useState} from 'react';

import debounce from 'lodash.debounce';

import {Icon, Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';

const MAX_LEN = 200;

type Props = {
    id: string,
    value: string,
    onChange: (value: string) => void,
    onClear: () => void,
    timeout: number,
    width?: number,
    placeholder?: string,
    disabled?: boolean,
};

export const FilterInput: React.FC<Props> = ({id, value, placeholder, onChange, disabled, width, onClear, timeout}) => {
    const [innerState, setInnerState] = useState<string>(value);
    React.useEffect(() => {
        setInnerState(value);
    }, [value]);
    const debouncedChange = useMemo(() => debounce((v: string) => onChange(v), timeout), [onChange, timeout]);
    const handleInput: React.FormEventHandler<HTMLInputElement> = useCallback(e => {
        const v = e.currentTarget.value;
        if (v !== ' ') {
            setInnerState(v);
            debouncedChange(v);
        }
    }, [debouncedChange]);
    const handleClear = useCallback(() => {
        setInnerState(''); onClear();
    }, [onClear]);
    const endAdornment = useMemo(() => (
        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            {innerState ? <Icon type={Icons.CLOSE} size="Size_19" onClick={handleClear} style={{cursor: 'pointer'}}/> : null}
            <Icon type={Icons.SEARCH} size="Size_14"/>
        </div>
    ), [innerState, handleClear]);
    const handleBlur = useCallback(() => {
        const t = innerState.trim(); onChange(t); setInnerState(t);
    }, [innerState, onChange]);
    const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    }, []);
    return (
        <div style={{width}}>
            <Input id={id} value={innerState} placeholder={placeholder} autoComplete="off" autoCorrect="off" onInput={handleInput} disabled={disabled} endAdornment={endAdornment} onBlur={handleBlur} onKeyUp={handleKeyUp} maxLength={MAX_LEN}/>
        </div>
    );
};
